/**
 * Generic time-series line chart for the dashboard.
 * Renders one or more color-coded data series over time buckets
 * with legend hover emphasis.
 *
 * @module components/TimeSeriesChart
 */

import React, { useMemo, useState }  from 'react';
import { LineChart,
         Line,
         XAxis,
         YAxis,
         CartesianGrid,
         Tooltip,
         Legend,
         ResponsiveContainer } from 'recharts';

/** Bucket width in milliseconds for each interval. */
const BUCKET_WIDTH_MS = {
  hour:  5 * 60 * 1000,
  day:   60 * 60 * 1000,
  week:  4 * 60 * 60 * 1000,
  month: 24 * 60 * 60 * 1000,
};

/**
 * Formats an epoch-ms timestamp for the X-axis based on interval and timezone.
 *
 * @param {number}  epoch        - Epoch milliseconds.
 * @param {string}  interval     - One of 'hour', 'day', 'week', 'month'.
 * @param {boolean} useLocalTime - If true, format in browser local time; otherwise UTC.
 * @returns {string} Formatted time label.
 */
function formatBucket(epoch, interval, useLocalTime) {
  const d  = new Date(epoch);
  const hh = String(useLocalTime ? d.getHours()  : d.getUTCHours()).padStart(2, '0');
  const mm = String(useLocalTime ? d.getMinutes() : d.getUTCMinutes()).padStart(2, '0');

  if (interval === 'hour') return `${hh}:${mm}`;
  if (interval === 'day')  return `${hh}:${mm}`;

  const tz  = useLocalTime ? undefined : 'UTC';
  const mon = d.toLocaleString('en-US', { month: 'short', ...(tz && { timeZone: tz }) });
  const day = useLocalTime ? d.getDate() : d.getUTCDate();

  if (interval === 'week') return `${mon} ${day} ${hh}:00`;
  return `${mon} ${day}`;
}

/**
 * Generates tick positions at every bucket boundary within [start, end].
 *
 * @param {number} start    - Range start epoch ms.
 * @param {number} end      - Range end epoch ms.
 * @param {string} interval - Time interval key.
 * @returns {number[]} Array of epoch-ms tick positions.
 */
function generateTicks(start, end, interval) {
  const step  = BUCKET_WIDTH_MS[interval] || BUCKET_WIDTH_MS.day;
  const ticks = [];
  for (let t = start; t <= end; t += step) {
    ticks.push(t);
  }
  return ticks;
}

/**
 * Renders a time-series line chart with legend hover emphasis.
 *
 * @param {object}   props
 * @param {object[]} props.data            - Recharts-ready data array (each entry must have a `time` key).
 * @param {string}   props.title           - Chart title text.
 * @param {string[]} props.series          - Ordered list of data keys to plot as lines.
 * @param {Object.<string, string>} props.colors - Map of series key to hex color.
 * @param {function} props.legendFormatter - (seriesKey) => display label for the legend.
 * @param {string}   props.interval        - Time interval (hour/day/week/month).
 * @param {number}   props.rangeStart      - Epoch ms for x-axis domain start.
 * @param {number}   props.rangeEnd        - Epoch ms for x-axis domain end.
 * @param {boolean}  props.useLocalTime    - Whether to display in browser local time.
 * @returns {React.ReactElement|null}
 */
export default function TimeSeriesChart({ data, title, series, colors,
                                          legendFormatter, interval,
                                          rangeStart, rangeEnd, useLocalTime }) {
  const [hoveredSeries, setHoveredSeries] = useState(null);

  const ticks = useMemo(() => {
    if (rangeStart == null || rangeEnd == null) return undefined;
    return generateTicks(rangeStart, rangeEnd, interval);
  }, [rangeStart, rangeEnd, interval]);

  if (!data || data.length === 0) {
    return null;
  }

  const domain = (rangeStart != null && rangeEnd != null)
    ? [rangeStart, rangeEnd]
    : ['dataMin', 'dataMax'];

  return (
    <div className="dashboard-chart-section">
      <h3 className="dashboard-chart-title">{title}</h3>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis
            dataKey="time"
            type="number"
            domain={domain}
            ticks={ticks}
            tickFormatter={(epoch) => formatBucket(epoch, interval, useLocalTime)}
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
          />
          <Tooltip
            labelFormatter={(epoch) => formatBucket(epoch, interval, useLocalTime)}
            contentStyle={{
              backgroundColor: 'var(--color-card-bg)',
              border:          '1px solid var(--color-border)',
              borderRadius:    '6px',
            }}
          />
          <Legend
            formatter={legendFormatter}
            onMouseEnter={(e) => setHoveredSeries(e.dataKey)}
            onMouseLeave={() => setHoveredSeries(null)}
          />
          {series.map(key => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={key}
              stroke={colors[key]}
              strokeWidth={hoveredSeries === key ? 4 : 2}
              strokeOpacity={hoveredSeries && hoveredSeries !== key ? 0.3 : 1}
              dot={{ r: hoveredSeries === key ? 5 : 3 }}
              activeDot={{ r: 5 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export { formatBucket, generateTicks, BUCKET_WIDTH_MS };
